import React from 'react';

const Terms = () => (
	<div>
		<h2>General</h2>
		<p className="p1">
			At Noteable, we care about your personal data, so we have prepared this Privacy Policy to
			explain how we collect, use and share it.
		</p>
		<p className="p1">
			This Privacy Policy (“<strong>Privacy Policy</strong>”) details the personal data Noteable
			(“Noteable”, “<strong>we</strong>”, “<strong>us</strong>” or “<strong>our</strong>”) receives
			about you, how we process it and your rights and obligations in relation to your personal
			data. Noteable, a company registered at Sturlasgade 12B, 2300 Copenhagen S, Denmark is the
			data controller for the purposes of the General Data Protection Regulation (“
			<strong>GDPR</strong>”) and any relevant local legislation (“
			<strong>Data Protection Laws</strong>”).
		</p>
		<p className="p1">
			By using or accessing the Service, you agree to the terms of this Privacy Policy. Capitalized
			terms not defined here have the meanings set forth in the terms and conditions (the “
			<strong>Terms and Conditions</strong>”), located at noteable.co/privacy. We may update our
			Privacy Policy to reflect changes to our information practices. If we do this and the changes
			are material, we will post a notice that we have made changes to this Privacy Policy on the
			Website for at least 7 days before the changes are made, and we will indicate the date these
			terms were last revised at the bottom of the Privacy Policy. Any revisions to this Privacy
			Policy will become effective at the end of that 7 day period.
		</p>
		<h3 className="p1">
			<span className="s1">Information We Collect</span>
		</h3>
		<p className="p1">
			This Privacy Policy explains how we collect, use and share your personal data.
		</p>
		<p className="p1">
			<strong>Information you provide</strong>
		</p>
		<p className="p1">
			Through the registration process, you will provide us with your name, e-mail and if you chose
			to update your profile also your profession, education level and school. You will also provide
			us with your payment transaction information if you choose to subscribe for Noteable services.
		</p>
		<p className="p1">
			<strong>Activity Data</strong>
		</p>
		<p className="p1">
			When you use the Service, you will submit information and content to your profile. We will
			also generate data about your use of our Services including your tastings, ratings and events.
			We also collect technical data about how you interact with our Services; for more information,
			see&nbsp;<span className="s1">Cookies</span>.
		</p>
		<p className="p1">&nbsp;</p>
		<p className="p1">
			<strong>Product Research and Development</strong>
		</p>
		<p className="p1">
			We may contact you to participate in product test and research activities. These may include
			surveys, interviews, and other types of feedback sessions. When you participate in these
			activities, the information you provide will be used to test, improve, and develop our
			products. We will record the video, audio and text transcriptions of this feedback together
			with any additional contact information you provide and will retain this data for two years.
		</p>
		<p className="p2">
			<span className="s1">Please contact us at&nbsp;</span>
			<a href="mailto:christina@noteablo.co">
				<span className="s2">christina@noteable.co</span>
			</a>
		</p>
		<ul className="ul1">
			<ul className="ul1">
				<li className="li3">Request more information about the research activities</li>
				<li className="li3">Opt out of being contacted for research activities.</li>
				<li className="li3">
					Request a copy of any research related data you have provided to us.
				</li>
			</ul>
		</ul>
		<h3 className="p1">
			<span className="s1">Cookies</span>
		</h3>
		<p className="p1">
			When you enter the Website, we collect your browser type and your IP address (a unique address
			that identifies your computer on the Internet). When you use our mobile Application, we
			collect your device type, your device ID, and your IP address.
		</p>
		<p className="p1">
			In addition, we store certain information from your browser using “cookies.” A cookie is a
			piece of data stored on the user’s computer tied to information about the user. We use session
			ID cookies to confirm that users are logged in. If you do not want information collected
			through the use of cookies, there is a simple procedure in most browsers that allows you to
			deny or accept the cookie feature; however, you should note that cookies may be necessary to
			provide you certain features available on the Website (e.g., customized delivery of
			information).
		</p>
		<p className="p1">
			You can find more information about cookies and how to manage them at&nbsp;
			<a href="http://www.allaboutcookies.org./" target="_blank" rel="noopener noreferrer">
				<span className="s1">http://www.allaboutcookies.org</span>.
			</a>
		</p>
		<h3 className="p1">Information Obtained by Third Parties</h3>
		<p className="p1">
			To support and enhance the Service, we work with a variety of third party advertising
			networks, marketing analytics service providers and website analysis firms, such as Google,
			Facebook etc (collectively, “<strong>Third Party Advertising Service Providers</strong>”).
			These Third Party Advertising Service Providers collect and use personal data about your
			visits to and use of the Website and mobile Application, as well as other websites in order to
			serve you with advertisements and content tailored to meet your preferences and likely
			interests or better understand ad effectiveness. Aggregate demographic and interest data will
			also be used for market research purposes.
		</p>
		<p className="p1">
			We use Google Analytics which is a web analytics tool that helps us understand how users
			engage with the Website. Like many services, Google Analytics uses first-party cookies to
			track user interactions as in our case, where they are used to collect information about how
			users use our site. This information is used to compile reports and to help us improve our
			Website. The reports disclose website trends without identifying individual visitors. You can
			opt out of Google Analytics without affecting how you visit our site – for more information on
			opting out of being tracked by Google Analytics across all websites you use, visit this Google
			page:&nbsp;
			<a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">
				<span className="s1">https://tools.google.com/dlpage/gaoptout</span>
			</a>
			.
		</p>
		<p className="p1">
			You can opt-out of receiving personalized ads from advertisers and ad networks that are
			members of the Network Advertising Initiative (“NAI”) or who follow the DAA Self-Regulatory
			Principles for Online Behavioral Advertising using their respective opt-out tools. The NAI’s
			opt-out tool can be found here&nbsp;
			<a
				href="http://www.networkadvertising.org/choices/"
				target="_blank"
				rel="noopener noreferrer"
			>
				<span className="s1">http://www.networkadvertising.org/choices/</span>
			</a>
			&nbsp;and the DAA’s opt out tool can be found here&nbsp;
			<a href="http://www.aboutads.info/choices/" target="_blank" rel="noopener noreferrer">
				<span className="s1">http://www.aboutads.info/choices/</span>
			</a>
			.
		</p>
		<p className="p1">
			In addition, your mobile devices may offer settings that enable you to make choices about the
			collection, use, or transfer of mobile app information for online behavioral advertising (for
			example, Apple iOS’ Advertising ID and Google Android’s Advertising ID). Please note that
			opting out does not prevent the display of all advertisements to you.
		</p>
		<h3 className="p1">Use of information obtained by Noteable</h3>
		<p className="p2">
			Noteable may occasionally send you service related and product change announcements through
			the general operation of the Service. We will send you reminder notifications to support the
			noteable tasting methodology. We process your data to help provide you with the Service and
			offer personalized features, to understand and improve our Service and to keep our Service
			safe and secure.
		</p>
		<p className="p2">
			Noteable may use or share anonymous data collected through the Service, including Activity
			Data without limitation. As a business, it is critical that we perform our contract with you
			with the best service possible, and it is in our legitimate interests to perform these
			processing functions and to enable service e-mails by default to keep your data secure and
			provide our Service. You may opt out of any non-essential service e-mails at any time.
		</p>
		<p className="p2">
			We may also use your contact information to send you notifications regarding new services,
			offers and promotions offered by notable if you affirmatively consent to receive such
			communications.
		</p>
		<h3 className="p2">Sharing your personal data with third parties</h3>
		<p className="p2">
			Noteable shares your personal data only when it is necessary to offer the Service, legally
			required, or permitted by you.
		</p>
		<p className="p2">
			We will provide personal data to hosting providers such as Amazon Web Services, search engine
			providers such as Google and analytics providers.
		</p>
		<p className="p2">
			These data processors help us bring you the Service. For example, we may share your
			information in order to detect where or how you encountered a bug when using our mobile
			application. In connection with these operations, our service providers will have access to
			personal data for a limited time. When we utilize service providers for processing any
			personal data, we implement contractual protections limiting the use of that personal data to
			the provision of services to Noteable.
		</p>
		<p className="p2">
			We will be required to access and disclose personal data in response to lawful requests, such
			as subpoenas or court orders, or in compliance with applicable laws. Additionally, we will
			access and share account or other personal data when we believe it is necessary to comply with
			law, to protect our interests or property, to prevent fraud or other illegal activity
			perpetrated through the Service or using the Noteable name, or to prevent imminent harm. This
			will include accessing and sharing personal data with other companies, lawyers, agents or
			government agencies.
		</p>
		<p className="p2">
			If the ownership of all or substantially all of the Noteable business, were to change, your
			personal data will be transferred to the new owner. In any such transfer of information, your
			personal data would remain subject to this section.
		</p>
		<p className="p2">
			Noteable will share aggregate or anonymous data collected through the Service, including
			Activity Data, for purposes such as understanding or improving the service.
		</p>
		<h3 className="p2">Data subject rights and data retention</h3>
		<p className="p2">
			You can manage your account settings at noteable.co/profile to update, amend, and correct your
			information.
		</p>
		<p className="p2">
			You also have the following rights in relation to the personal data we hold about you, unless
			provided otherwise by local law:
		</p>
		<ul className="ul1">
			<ul className="ul1">
				<li className="li3">
					To request access to, or erasure of, the personal data we hold about you.
				</li>
				<li className="li3">
					To request us to restrict the processing of the personal data we hold about you.
				</li>
				<li className="li3">To object to us processing personal data relating to you.</li>
				<li className="li3">
					Where you have given us consent to process your personal data, you have the right to
					withdraw that consent at any time.
				</li>
				<li className="li3">
					To export the personal data you have provided to Noteable in a format that can be
					transferred electronically to a third party.
				</li>
				<li className="li3">
					To delete your account with Noteable by following the instructions available through the
					Service.
				</li>
			</ul>
		</ul>
		<p className="p2">
			Noteable will retain your data until your account is deleted, after which point we will retain
			anonymous data collected through the Service, including Activity Data, which may be used by
			Noteable and shared with third parties in any manner.
		</p>
		<p className="p2">
			You may also have the right to make a GDPR complaint to the relevant Supervisory Authority. A
			list of Supervisory Authorities is available here:&nbsp;
			<a
				href="http://ec.europa.eu/justice/data-protection/bodies/authorities/index_en.htm"
				target="_blank"
				rel="noopener noreferrer"
			>
				<span className="s1">
					http://ec.europa.eu/justice/data-protection/bodies/authorities/index_en.htm
				</span>
			</a>
			. If you need further assistance regarding your rights, please contact us using the contact
			information provided below and we will consider your request in accordance with applicable
			law.
		</p>
		<p>
			<strong>Third party websites and links</strong>
		</p>
		<p className="p2">
			Please note that you may have cookies placed on your computer by third party websites that
			refer you to our Service. Although we do not share your personal data with these third party
			websites unless it is reasonably necessary to offer the Service, they may be able to link
			certain non-personally identifiable information we transfer to them with personal data they
			previously collected from you. Please review the privacy policies of each website you visit to
			better understand their privacy practices. In addition, Noteable would like to inform you that
			anytime you click on links (including advertising banners), which take you to third party
			websites, you will be subject to the third parties’ privacy policies.
		</p>
		<p className="p2">
			Our Services contain a few links to other sites operated by third parties. Noteable does not
			control such other sites and is not responsible for their content, their privacy policies, or
			their use of personal data, including any personal or financial information collected by our
			third party payment processor to process payments for in-app purchases. Noteable’s inclusion
			of such links does not imply any endorsement of the content on such sites or of their owners
			or operators except as disclosed on the Services. Any information submitted by you directly to
			these third parties is subject to that third party’s privacy policy.
		</p>
		<h2 className="p3">Data protection</h2>
		<p className="p2">
			In compliance with the EU-US and Swiss-US Privacy Shield Principles, Noteable commits to
			resolve complaints about your privacy and our collection or use of your personal data.
			European Union or Swiss individuals with inquiries or complaints regarding this Privacy
			Statement should first contact Noteable’s Data Protection Officer at:
		</p>
		<p className="p2">
			Data Protection Officer Noteable, Steffen Rind Helsbro, Sturlasgade 12B, 2300 Copenhagen S,
			Denmark.<span className="Apple-converted-space">&nbsp;&nbsp;</span>Phone:&nbsp;
			<a href="tel:+4520259900">+45 20 25 99 00</a>
			<br />
			E-mail:&nbsp;<a href="mailto:steffen@noteable.co">steffen@noteable.co</a>
		</p>
		<p className="p2">
			Noteable has further committed to refer unresolved privacy complaints under the Privacy Shield
			Principles to an independent dispute resolution mechanism, the BBB EU PRIVACY SHIELD, operated
			by the Council of Better Business Bureaus. If you do not receive timely acknowledgement of
			your complaint, or if your complaint is not satisfactorily addressed, please visit&nbsp;
			<a
				href="https://www.bbb.org/EU-privacy-shield/for-eu-consumers"
				target="_blank"
				rel="noopener noreferrer"
			>
				<span className="s1">https://www.bbb.org/EU-privacy-shield/for-eu-consumers</span>
			</a>
			&nbsp;for more information and to file a complaint. The services of BBB EU PRIVACY SHIELD are
			provided at no cost to you.
		</p>
		<p className="p5">
			<span className="s2">
				Contact details for the EU data protection authorities can be found at&nbsp;
			</span>
			<a
				href="http://ec.europa.eu/justice/data-protection/bodies/authorities/index_en.htm"
				target="_blank"
				rel="noopener noreferrer"
			>
				<span className="s3">
					http://ec.europa.eu/justice/data-protection/bodies/authorities/index_en.htm
				</span>
			</a>
			<span className="s2">.</span>
		</p>
		<p className="p2">
			If your Privacy Shield complaint cannot be resolved through the above teams, under certain
			conditions, you may invoke binding arbitration for some residual claims not resolved by other
			redress mechanisms. See Privacy Shield Annex 1 at&nbsp;
			<a
				href="https://www.privacyshield.gov/article?id=ANNEX-I-introduction"
				target="_blank"
				rel="noopener noreferrer"
			>
				<span className="s1">https://www.privacyshield.gov/article?id=ANNEX-I-introduction</span>
			</a>
			.
		</p>
		<h3 className="p2">Links</h3>
		<p className="p2">
			The Service may contain links to other websites. We are not responsible for the privacy
			practices of other websites. We encourage users to be aware when they leave the Service to
			read the privacy statements of other websites that collect personally identifiable
			information. This Privacy Policy applies only to information collected by Noteable via the
			Service.
		</p>
		<h3 className="p2">Information security</h3>
		<p className="p2">
			Noteable has implemented administrative and technical safeguards it believes are appropriate
			to protect the confidentiality, integrity and availability of your personal data. However,
			given sufficient resources, a determined attacker could defeat those safeguards and may, as a
			result, gain access to the data we seek to protect.
		</p>
		<h3 className="p2">Do Not Track</h3>
		<p className="p2">
			The Service is not designed to respond to “do not track” signals sent by some browsers.
		</p>
		<h3>Contact Us</h3>
		<p className="p2">Noteables headquarters are located in Denmark at:</p>
		<p className="p2">
			Noteable
			<br />
			Sturlasgade 12B
			<br />
			2300 Copenhagen S<br />
			Denmark
			<br />
			Phone:&nbsp;<a href="tel:+4527112719">+ 45 27 11 27 19</a>
		</p>
		<p className="p2">
			For all data privacy inquiries and any questions or concerns you have about this Privacy
			Policy, please contact our Data Protection Officer at&nbsp;
			<a href="mailto:steffen@noteable.co">steffen@noteable.co</a>
		</p>
		<p className="p7">Last revised june, 2019</p>
		<br />
		<br />
		<br />
	</div>
);

export default Terms;
